import { useField } from 'formik';
import React, { useState, KeyboardEvent } from 'react';
import InputDescription from 'src/component/Input/InputDescription';
import InputError from 'src/component/Input/InputError';
import InputLabel from 'src/component/Input/InputLabel';

interface OwnProps {
  label?: string;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  name: string;
  suggestions?: string[];
}

const InputTags = ({
  name,
  description,
  label,
  placeholder = 'Type and press Enter to add',
  disabled = false,
  suggestions = [],
}: OwnProps) => {
  const [field, , helpers] = useField<string[]>(name);
  const [inputValue, setInputValue] = useState('');
  const inputId = `inputTags_${name}`;

  const tags = field.value || [];

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      helpers.setValue([...tags, trimmedTag]);
    }
    setInputValue('');
  };

  const removeTag = (indexToRemove: number) => {
    helpers.setValue(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  // Filter suggestions that are not already selected
  const availableSuggestions = suggestions.filter((s) => !tags.includes(s));

  return (
    <div className="form-group">
      <InputLabel forInput={inputId} text={label} />
      <div
        className="form-control d-flex flex-wrap align-items-center"
        style={{ minHeight: '38px', height: 'auto', padding: '4px 8px' }}
      >
        {tags.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="badge bg-primary me-1 mb-1 d-inline-flex align-items-center"
            style={{ fontSize: '0.9em', padding: '4px 8px' }}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => removeTag(index)}
                aria-label="Remove"
              />
            )}
          </span>
        ))}
        {!disabled && (
          <input
            id={inputId}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => inputValue && addTag(inputValue)}
            placeholder={tags.length === 0 ? placeholder : ''}
            className="border-0 flex-grow-1"
            style={{ outline: 'none', minWidth: '100px' }}
          />
        )}
      </div>
      {availableSuggestions.length > 0 && !disabled && (
        <div className="mt-2">
          <small className="text-muted d-block mb-1">Available options:</small>
          <div className="d-flex flex-wrap gap-1">
            {availableSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => addTag(suggestion)}
              >
                + {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      <InputError name={name} />
      <InputDescription description={description} />
    </div>
  );
};

export default InputTags;

